<template>
  <div class="chart-container">
    <div class="chart-header">
      <Dropdown 
        :titleDropdown="selectedChart.name"
        position="right"
        colorArrow="black"
      >
        <ul>
          <li 
            v-for="chart in availableCharts" 
            :key="chart.type"
            @click="changeChartType(chart.type)"
            class="bkps-dropdown-item"
            :title="chart.description"
          >
            <i :class="chart.icon"></i>
            {{ chart.name }}
          </li>
        </ul>
      </Dropdown>
      
      <Dropdown 
        :titleDropdown="selectedSort.name"
        position="right"
        colorArrow="black"
        class="ml-2"
      >
        <ul>
          <li 
            v-for="sort in availableSorts" 
            :key="sort.type"
            @click="changeSortType(sort.type)"
            class="bkps-dropdown-item"
            :title="sort.description"
          >
            <i :class="sort.icon"></i>
            {{ sort.name }}
          </li>
        </ul>
      </Dropdown>
    </div>
    <div ref="targetChart" style="height: 350px; width: 100%; margin-top: 10px;"></div>
  </div>
</template>

<script>
import Highcharts from 'highcharts/es5/highcharts'
import ChartMix from '@/components/charts/ChartMix.vue'
import ChartThemeMixin from '@/components/charts/ChartThemeMixin.vue'
import Dropdown from '@/components/shares/specialTag/Dropdown.vue'

import { IssuingAuthority } from '@/models/IssuingAuthority'

export default {
  components: { Dropdown },
  props: {
    graphData: { required: true, type: Array },
    graphTitle: { required: true, type: String },
    loading: { required: true, type: Boolean },
  },
  data() {
    return {
      chartType: 'semi-donut',
      sortBy: 'value',
      availableCharts: [
        {
          type: 'semi-donut',
          name: 'Semi Rosca',
          icon: 'fas fa-chart-pie',
          description: 'Gráfico semi rosca'
        },
        {
          type: 'pie',
          name: 'Pizza',
          icon: 'fas fa-chart-pie',
          description: 'Gráfico de pizza'
        },
        {
          type: 'donut',
          name: 'Rosca',
          icon: 'fas fa-dot-circle',
          description: 'Gráfico donut'
        },
        {
          type: 'column',
          name: 'Barras',
          icon: 'fas fa-chart-bar',
          description: 'Gráfico de barras'
        },
        {
          type: 'bar',
          name: 'Horizontais',
          icon: 'fas fa-chart-bar',
          description: 'Gráfico de barras horizontais'
        }
      ],
      availableSorts: [
        {
          type: 'value',
          name: 'Valor',
          icon: 'fas fa-dollar-sign',
          description: 'Ordenar por valor'
        },
        {
          type: 'count',
          name: 'Qtd',
          icon: 'fas fa-hashtag',
          description: 'Ordenar por quantidade'
        }
      ]
    }
  },
  computed: {
    selectedChart() {
      return this.availableCharts.find(chart => chart.type === this.chartType)
    },
    selectedSort() {
      return this.availableSorts.find(sort => sort.type === this.sortBy)
    }
  },
  methods: {
    changeChartType(type) {
      this.chartType = type
      this.createChart()
    },
    changeSortType(type) {
      this.sortBy = type
      this.createChart()
    },
    buildSeries() {
      const serie = []
      const calculatePercentage = (value, total) => { return (value * 100) / total }

      if (this.sortBy === 'value') {
        const total = this.graphData.map(t => t.sum).reduce((n, n1) => n + n1, 0)

        this.graphData.forEach(item => {
          const autuadorName = IssuingAuthority.label(item.autuador)
          const valueInReais = item.sum / 100
          const percentage = calculatePercentage(item.sum, total)

          serie.push({
            name: autuadorName,
            y: percentage,
            rawValue: valueInReais,
            count: item.count
          })
        })
      } else {
        const totalCount = this.graphData.map(t => t.count).reduce((n, n1) => n + n1, 0)

        this.graphData.forEach(item => {
          const autuadorName = IssuingAuthority.label(item.autuador)
          const valueInReais = item.sum / 100
          const percentage = calculatePercentage(item.count, totalCount)

          serie.push({
            name: autuadorName,
            y: percentage,
            rawValue: valueInReais,
            count: item.count
          })
        })
      }

      // Ordena por valor decrescente e pega apenas os top 10
      serie.sort((a, b) => b.y - a.y)
      const topSerie = serie.slice(0, 10)

      return topSerie
    },
    createChart() {
      const theme = this.chartTheme
      const serie = this.buildSeries()
      const isPie = ['pie', 'donut', 'semi-donut'].includes(this.chartType)

      const colorConnectors = function (chart) {
        const series = chart.series && chart.series[0]
        if (!series) return
        series.points.forEach(function (point) {
          if (point.connector) {
            point.connector.attr({ stroke: point.color })
          }
        })
      }
      const isBar = ['column', 'bar'].includes(this.chartType)

      const chartConfig = {
        chart: {
          backgroundColor: theme.backgroundColor,
          plotBackgroundColor: null,
          plotBorderWidth: 0,
          plotShadow: false,
          type: isPie ? 'pie' : this.chartType,
          height: isPie ? 350 : 400,
          margin: isPie ? [60, 100, 60, 100] : undefined,
          style: { fontFamily: '"Montserrat", sans-serif' }
        },
        colors: theme.colors,
        credits: {
          enabled: false
        },
        title: {
          text: this.currentTitle,
          align: 'center',
          style: {
            color: theme.textColor,
            fontSize: '18px',
            fontWeight: '600'
          }
        },
        tooltip: {
          backgroundColor: theme.backgroundColor,
          borderColor: theme.gridLineColor,
          style: { color: theme.textColor },
          headerFormat: '',
          pointFormat: `<span style="color:{point.color}">{point.name}</span><br/>
                       Porcentagem: <b>{point.y:.2f}%</b><br/>
                       Valor: R$ {point.rawValue:,.2f}<br/>
                       Quantidade: {point.count}`
        },
        accessibility: {
          point: {
            valueSuffix: isPie ? '%' : ''
          }
        },
        plotOptions: {},
        series: [{
          name: 'Autuadores',
          type: isPie ? 'pie' : this.chartType,
          data: serie,
          colorByPoint: true
        }]
      }

      if (this.chartType === 'semi-donut') {
        chartConfig.chart.events = { render: function () { colorConnectors(this) } }
        chartConfig.chart.margin = [80, 80, 20, 80]
        chartConfig.plotOptions.pie = {
          dataLabels: {
            enabled: true,
            distance: 25,
            connectorWidth: 2,
            format: '{point.name}: {point.percentage:.1f}%',
            style: {
              fontWeight: '600',
              color: theme.textColor,
              textOutline: 'none',
              fontSize: '10px'
            }
          },
          startAngle: -90,
          endAngle: 90,
          center: ['50%', '75%'],
          size: '110%',
          borderWidth: 2,
          borderColor: theme.backgroundColor
        }
        chartConfig.series[0].innerSize = '50%'
      } else if (this.chartType === 'donut') {
        chartConfig.chart.events = { render: function () { colorConnectors(this) } }
        chartConfig.plotOptions.pie = {
          allowPointSelect: true,
          cursor: 'pointer',
          borderRadius: 8,
          borderWidth: 3,
          borderColor: theme.backgroundColor,
          dataLabels: {
            enabled: true,
            distance: 30,
            connectorWidth: 2,
            format: '{point.name}: {point.percentage:.1f}%',
            style: {
              fontWeight: '600',
              color: theme.textColor,
              textOutline: 'none'
            }
          },
          innerSize: '60%',
          size: '80%'
        }
        chartConfig.series[0].innerSize = '60%'
        chartConfig.legend = { enabled: false }
      } else if (this.chartType === 'pie') {
        chartConfig.chart.events = { render: function () { colorConnectors(this) } }
        chartConfig.plotOptions.pie = {
          dataLabels: {
            enabled: true,
            distance: 30,
            connectorWidth: 2,
            format: '{point.name}: {point.percentage:.1f}%',
            style: {
              color: theme.textColor,
              textOutline: 'none',
              fontWeight: '600',
              fontSize: '10px'
            }
          },
          size: '80%'
        }
        chartConfig.legend = { enabled: false }
      }

      if (isBar) {
        const categories = serie.map(item => item.name)
        const data = serie.map(item => ({
          y: item.y,
          rawValue: item.rawValue,
          count: item.count,
          name: item.name
        }))

        chartConfig.chart.marginTop = 60
        chartConfig.chart.marginBottom = this.chartType === 'column' ? 100 : 60
        chartConfig.xAxis = {
          categories: categories,
          title: this.chartType === 'bar' ? {
            text: 'Órgão Autuador',
            style: { color: theme.textColor, fontSize: '11px' }
          } : undefined,
          labels: {
            rotation: this.chartType === 'column' ? -45 : 0,
            style: {
              fontSize: '10px',
              color: theme.textColor
            }
          }
        }
        chartConfig.yAxis = {
          title: {
            text: this.sortBy === 'value' ? 'Percentual por Valor' : 'Percentual por Quantidade',
            style: { color: theme.textColor }
          },
          labels: { style: { color: theme.textColor } },
          gridLineColor: theme.gridLineColor
        }
        const barDataLabels = {
          enabled: true,
          format: '{point.y:.2f}%',
          style: {
            color: theme.textColor,
            textOutline: 'none',
            fontSize: '11px'
          },
          distance: 0
        }
        chartConfig.plotOptions.series = {
          borderRadius: 5,
          dataLabels: barDataLabels
        }
        chartConfig.plotOptions.column = {
          borderRadius: 6,
          borderWidth: 0,
          dataLabels: barDataLabels
        }
        chartConfig.plotOptions.bar = {
          borderRadius: 6,
          borderWidth: 0,
          dataLabels: barDataLabels
        }
        chartConfig.legend = { enabled: false }
        chartConfig.series[0].data = data
      }

      Highcharts.chart(this.$refs.targetChart, chartConfig)
    }
  },
  mixins: [ChartMix, ChartThemeMixin],
  mounted() {
    this.createChart()
  },
  watch: {
    isDarkTheme: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      immediate: false
    },
    graphData: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      deep: true
    }
  },
  name: 'CmEmbarcadorAutuadorChart'
}
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: visible;
  background-color: var(--white-bg);
  padding: 15px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
}

.chart-header {
  position: absolute;
  top: 15px;
  right: 20px;
  z-index: 10;
  display: flex;
  gap: 8px;
}

/* CSS específico para o dropdown dos gráficos */
.chart-header .bkps-dropdown {
  background: var(--white-bg) !important;
  border: 1px solid var(--border-color) !important;
  color: var(--primary-text-color) !important;
}

.chart-header .bkps-dropdown-content {
  background-color: var(--white-bg) !important;
  color: var(--primary-text-color) !important;
  border: 1px solid var(--border-color) !important;
}

.chart-header .bkps-dropdown-item {
  color: var(--primary-text-color) !important;
}

.chart-header .bkps-dropdown-item:hover {
  background-color: var(--btn-hover-bg) !important;
}

.chart-header .hide {
  max-height: 0 !important;
  padding: 0 !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
}
</style>
